import type {
  AuditTypesFilter,
  AuditTypesParams,
  AuditTypesQueryInput,
} from '../-types';

/**
 * The columns the list maps into its `ORDER BY` — `AuditTypesListSortColumns`
 * in the audits contract. The querystring schema rejects any other key with a
 * 400, so an unlisted sort is dropped here rather than sent.
 */
const SORTABLE_FIELDS = new Set([
  'id',
  'name',
  'isealMember',
  'providerName',
  'yearFounded',
  'location',
  'externalLink',
  'conformityAssessment',
  'auditCount',
  'updatedAt',
]);

/** Text filters passed through as typed. Only `name` is a `LIKE` server-side. */
const TEXT_FILTERS = [
  'id',
  'name',
  'description',
  'isealMember',
  'providerName',
  'location',
  'conformityAssessment',
] as const;

/** Reads a single-valued filter off the table's multi-value filter map. */
function first(
  filters: Record<string, string[]> | undefined,
  key: string,
): string | undefined {
  const value = filters?.[key]?.[0]?.trim();
  return value && value.length > 0 ? value : undefined;
}

/**
 * Turns table state into the endpoint's parameters.
 *
 * Unlike document types, **this route's `page` is 0-based**, so the table's
 * 1-based page is decremented.
 *
 * Filters travel as one JSON `filter` blob, as the old dashboard sent them.
 * `yearFounded` goes as a number and `isDeprecated` as a boolean: the builder
 * turns a boolean into `= 0|1` against the integer column, and a stray string
 * would compare text against an integer.
 */
export function toAuditTypesParams(
  input: AuditTypesQueryInput,
): AuditTypesParams {
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  const filter: AuditTypesFilter = {};
  for (const key of TEXT_FILTERS) {
    const value = first(input.filters, key);
    if (value) filter[key] = value;
  }

  const year = first(input.filters, 'yearFounded');
  if (year && /^\d+$/.test(year)) filter.yearFounded = Number(year);

  const deprecated = first(input.filters, 'isDeprecated');
  if (deprecated === 'true' || deprecated === 'false') {
    filter.isDeprecated = deprecated === 'true';
  }

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(Object.keys(filter).length > 0 ? { filter } : {}),
  };
}

export function buildAuditTypesQs(params: AuditTypesParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', JSON.stringify(params.filter));
  return qs.toString();
}
